import { Injectable } from "@angular/core";
import { InvoiceService } from "./invoice.service";

/** Pharmacy Review list UI state kept while visiting invoice detail. */
export interface InvoiceReviewListContext {
  pharmacyId: number;
  facilityId: number;
  billingDate: string;
  filterLabel: string;
  searchResident: string;
}

@Injectable({ providedIn: 'root' })
export class InvoiceCacheService {
  private static readonly STORAGE_KEY = 'solvo.invoiceReview.list';

  private invoiceList: any[] = [];
  private reviewListContext: InvoiceReviewListContext = null;
  private hydratedFromSession = false;

  constructor(
    private invoiceService: InvoiceService) {
    this.hydrateFromSession();
  }

  setInvoices(invoices: any[]) {
    this.invoiceList = invoices ? invoices : [];
    this.persistToSession();
  }

  getInvoices(): any[] {
    this.hydrateFromSession();
    return this.invoiceList;
  }

  setReviewListContext(context: InvoiceReviewListContext) {
    if (!context) {
      this.reviewListContext = null;
      this.persistToSession();
      return;
    }
    this.reviewListContext = {
      pharmacyId: Number(context.pharmacyId),
      facilityId: Number(context.facilityId),
      billingDate: context.billingDate,
      filterLabel: context.filterLabel || 'ALL',
      searchResident: context.searchResident != null ? context.searchResident : ''
    };
    this.persistToSession();
  }

  getReviewListContext(): InvoiceReviewListContext {
    this.hydrateFromSession();
    return this.reviewListContext;
  }

  /** True when a previously loaded Pharmacy Review list can be restored for this pharmacy/facility. */
  canRestoreReviewList(pharmacyId: number, facilityId: number): boolean {
    this.hydrateFromSession();
    if (!this.reviewListContext || !this.reviewListContext.billingDate) {
      return false;
    }
    if (Number(this.reviewListContext.pharmacyId) !== Number(pharmacyId)
      || Number(this.reviewListContext.facilityId) !== Number(facilityId)) {
      return false;
    }
    return this.invoiceList != null;
  }

  clearReviewListCache() {
    this.invoiceList = [];
    this.reviewListContext = null;
    this.removeSession();
  }

  getNextInvoice(currentId: number, pharmacyId: number, facilityId: number, patientId: number): any | null {
    this.hydrateFromSession();
    const index = this.findCurrentInvoiceIndex(currentId, pharmacyId, facilityId, patientId);
    return index >= 0 && index < this.invoiceList.length - 1
      ? this.invoiceList[index + 1]
      : null;
  }

  getPreviousInvoice(currentId: number, pharmacyId: number, facilityId: number, patientId: number): any | null {
    this.hydrateFromSession();
    const index = this.findCurrentInvoiceIndex(currentId, pharmacyId, facilityId, patientId);
    return index > 0
      ? this.invoiceList[index - 1]
      : null;
  }

  updateIdCurrentInvoice(currentId: number, pharmacyId: number, facilityId: number, patientId: number, newInvoice: any): void {
    this.hydrateFromSession();
    const index = this.findCurrentInvoiceIndex(currentId, pharmacyId, facilityId, patientId);
    if (index < 0) {
      console.error("Invoice not found");
      return;
    }
    this.invoiceList[index] = newInvoice;
    this.persistToSession();
  }

  findCurrentInvoiceIndex(currentId: number, pharmacyId: number, facilityId: number, patientId: number): number {
    this.hydrateFromSession();
    const id = Number(currentId);
    const pharmacy = Number(pharmacyId);
    const facility = Number(facilityId);
    const patient = Number(patientId);
    return this.invoiceList.findIndex(i =>
      Number(i.id) === id
      && Number(i.pharmacyId) === pharmacy
      && Number(i.facilityId) === facility
      && Number(i.patientId) === patient
    );
  }

  private hydrateFromSession() {
    if (this.hydratedFromSession) {
      return;
    }
    this.hydratedFromSession = true;
    if (this.invoiceList && this.invoiceList.length > 0 && this.reviewListContext) {
      return;
    }
    try {
      if (typeof sessionStorage === 'undefined' || sessionStorage == null) {
        return;
      }
      const raw = sessionStorage.getItem(InvoiceCacheService.STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed) {
        return;
      }
      if ((!this.invoiceList || this.invoiceList.length === 0) && parsed.invoices) {
        this.invoiceList = parsed.invoices;
      }
      if (!this.reviewListContext && parsed.context) {
        this.reviewListContext = parsed.context;
      }
    } catch (e) {
    }
  }

  private persistToSession() {
    this.hydratedFromSession = true;
    try {
      if (typeof sessionStorage === 'undefined' || sessionStorage == null) {
        return;
      }
      sessionStorage.setItem(InvoiceCacheService.STORAGE_KEY, JSON.stringify({
        invoices: this.stripLineItems(this.invoiceList),
        context: this.reviewListContext
      }));
    } catch (e) {
    }
  }

  private removeSession() {
    this.hydratedFromSession = true;
    try {
      if (typeof sessionStorage === 'undefined' || sessionStorage == null) {
        return;
      }
      sessionStorage.removeItem(InvoiceCacheService.STORAGE_KEY);
    } catch (e) {
    }
  }

  /** List UI and next/previous only need header rows; line items make sessionStorage too large. */
  private stripLineItems(invoices: any[]): any[] {
    if (!invoices || invoices.length === 0) {
      return [];
    }
    const copy = JSON.parse(JSON.stringify(invoices));
    for (let i = 0; i < copy.length; i++) {
      copy[i].invoiceItemDtos = [];
    }
    return copy;
  }
}
